import { useState } from 'react';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';

import Button from 'design-system/src/components/Button/Button';
import { RadioOption, RadioButton, RadioLabel, ScrollableRadioGroup } from 'design-system/src/components/Radio/Radio';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../../constants';
import { MissingFaxNumberModal } from './MissingFaxNumberModal';
import AddBatchProvidersModal from '../onboarding/onboardingStepModals/AddBatchProvidersModal';
import { client } from '../../api';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import { ExtraInfoTable } from 'design-system/src/components/ExtraInfoTable/ExtraInfoTable';
import { VerificationStatus } from './types';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Badge } from 'design-system/src/components/Badge/Badge';

type ConfirmFaxModalProps = {
  onCloseModal: () => void;
  submitMutation: UseMutationResult<void, Error, { fax_number: string }>;
  selection: string;
};

const ConfirmFaxModal = ({ onCloseModal, submitMutation, selection }: ConfirmFaxModalProps) => {
  return (
    <Modal onClose={onCloseModal}>
      <ModalTitle>Confirm fax number</ModalTitle>

      <ModalBody>
        <ExtraInfoTable data={[{ label: 'Fax number', value: selection }]} />
        <Typography styledAs="bodySmallDMSans">
          Please confirm the fax number is accurate. After sending, you will need to contact our team to make any
          updates.
        </Typography>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" size="sm" onClick={onCloseModal} disabled={submitMutation.isPending}>
          Back
        </Button>
        <Button
          size="sm"
          onClick={() => submitMutation.mutate({ fax_number: selection })}
          isLoading={submitMutation.isPending}
        >
          Confirm and send
        </Button>
      </ModalFooter>
    </Modal>
  );
};

type FaxSelectionProps = {
  faxNumbers: string[];
  selectedFaxNumber: string | null;
  setSelectedFaxNumber: (faxNumber: string) => void;
  setIsConfirmModalOpen: (isConfirmModalOpen: boolean) => void;
  setIsMissingFaxModalOpen: (isOpen: boolean) => void;
};

const FaxSelection = ({
  faxNumbers,
  selectedFaxNumber,
  setSelectedFaxNumber,
  setIsConfirmModalOpen,
  setIsMissingFaxModalOpen,
}: FaxSelectionProps) => {
  return (
    <div className="flex flex-col gap-4">
      {!faxNumbers.length ? (
        <Badge variant="error">No options available</Badge>
      ) : (
        <ScrollableRadioGroup $maxHeight="220px">
          {faxNumbers.map((fax) => {
            const optionId = `fax-option-${fax}`;
            return (
              <RadioOption key={optionId}>
                <RadioButton
                  type="radio"
                  id={optionId}
                  name={'fax-options'}
                  value={fax}
                  checked={selectedFaxNumber === fax}
                  onChange={(e) => {
                    setSelectedFaxNumber(e.target.value);
                  }}
                />
                <RadioLabel as="label" htmlFor={optionId}>
                  {fax}
                </RadioLabel>
              </RadioOption>
            );
          })}
        </ScrollableRadioGroup>
      )}
      <div className="flex items-center justify-between">
        <Button variant="secondary" size="sm" onClick={() => setIsMissingFaxModalOpen(true)}>
          I don't see my fax number
        </Button>
        <Button size="sm" onClick={() => setIsConfirmModalOpen(true)} disabled={!selectedFaxNumber}>
          Trigger fax
        </Button>
      </div>
    </div>
  );
};

export type FaxNumberStepProps = {
  /**
   * Class name for additional styling
   */
  className?: string;
  /**
   * Prescriber data
   */
  prescriberData: {
    verification_status: VerificationStatus;
    fax_numbers: string[];
    triggered_fax_number: string | null;
  };
};

/**
 * Component for selecting and triggering fax verification
 */
export const FaxNumberStep = ({ className, prescriberData }: FaxNumberStepProps) => {
  const [selectedFaxNumber, setSelectedFaxNumber] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isMissingFaxModalOpen, setIsMissingFaxModalOpen] = useState(false);
  const [isAddProvidersModalOpen, setIsAddProvidersModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const triggerFax = useMutation<void, Error, { fax_number: string }>({
    mutationFn: async ({ fax_number }) => {
      await client.post('/verification/trigger-fax', {
        fax_number,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriber-verifications'] });
      setIsConfirmModalOpen(false);
      toast.custom(() => <Toast variant="success">Verification fax sent.</Toast>);
    },
    onError: (error) => {
      console.error('Error triggering fax:', error);
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const reTriggerFax = useMutation<void, Error, { fax_number: string }>({
    mutationFn: async ({ fax_number }) => {
      await client.post('/verification/retrigger-message', {
        fax_number,
      });
    },
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Verification fax sent.</Toast>);
    },
    onError: (error) => {
      console.error('Error retriggering fax:', error);
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const renderContent = () => {
    if (
      prescriberData.verification_status === 'pending' ||
      prescriberData.verification_status === 'no_fax_numbers_available' ||
      prescriberData.verification_status === 'no_valid_fax_numbers'
    ) {
      return (
        <FaxSelection
          faxNumbers={prescriberData.fax_numbers}
          selectedFaxNumber={selectedFaxNumber}
          setSelectedFaxNumber={setSelectedFaxNumber}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
          setIsMissingFaxModalOpen={setIsMissingFaxModalOpen}
        />
      );
    } else {
      const triggeredFaxNumber = prescriberData.triggered_fax_number;
      if (!triggeredFaxNumber) {
        Sentry.captureException(new Error('Triggered fax number is null'));
        console.error('Triggered fax number is null');
        return (
          <ErrorMessage>
            An error occurred. Please reach out to our team at {PROVIDER_SUPPORT_CONTACT_INFO.email}.
          </ErrorMessage>
        );
      }
      return (
        <div className="flex items-center justify-between">
          <Typography>Fax sent to {triggeredFaxNumber}</Typography>
          <Button
            variant="link"
            onClick={() => {
              reTriggerFax.mutate({ fax_number: triggeredFaxNumber });
            }}
          >
            Retrigger fax
          </Button>
        </div>
      );
    }
  };

  return (
    <>
      <div className={className}>{renderContent()}</div>
      {isConfirmModalOpen && selectedFaxNumber && (
        <ConfirmFaxModal
          onCloseModal={() => {
            setIsConfirmModalOpen(false);
          }}
          submitMutation={triggerFax}
          selection={selectedFaxNumber}
        />
      )}
      {isMissingFaxModalOpen && (
        <MissingFaxNumberModal
          onClose={() => setIsMissingFaxModalOpen(false)}
          onAddProviders={() => {
            setIsMissingFaxModalOpen(false);
            setIsAddProvidersModalOpen(true);
          }}
        />
      )}
      {isAddProvidersModalOpen && (
        <AddBatchProvidersModal open={isAddProvidersModalOpen} onClose={() => setIsAddProvidersModalOpen(false)} />
      )}
    </>
  );
};

export default FaxNumberStep;
